/**
 * /es/areas-que-atendemos — Spanish service-area hub with the same
 * interactive map as the EN page but Spanish copy + ES deep links.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Phone } from 'lucide-react';
import EsSEO from '@/components/seo/EsSEO';
import AreasMap from '@/components/common/AreasMap';
import useSpanishTrackingNumber from '@/hooks/useSpanishTrackingNumber';

const SITE = 'https://fortworthdental.com';

const SpanishAreasPage = () => {
  const { displayNumber, telLink } = useSpanishTrackingNumber();

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Dentist',
    name: 'Fort Worth Dental',
    inLanguage: 'es-US',
    url: `${SITE}/es/areas-que-atendemos`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '4620 Citylake Blvd W',
      addressLocality: 'Fort Worth',
      addressRegion: 'TX',
      postalCode: '76132',
      addressCountry: 'US',
    },
    areaServed: [
      'Fort Worth, TX',
      'Arlington, TX',
      'Irving, TX',
      'Grand Prairie, TX',
      'Bedford, TX',
      'Hurst, TX',
      'Mansfield, TX',
      'Tarrant County',
      'Dallas County',
      'Parker County',
      'Johnson County',
      'Denton County',
      'Ellis County',
    ],
  };

  return (
    <>
      <EsSEO
        title="Áreas que Atendemos — Fort Worth Dental | 53 Ciudades Cercanas"
        description="Fort Worth Dental atiende con orgullo más de 53 ciudades incluyendo Fort Worth, Arlington, Irving, Grand Prairie, Bedford, Hurst, Mansfield y más. Más de 1,100 reseñas de 5 estrellas."
        canonical="/es/areas-que-atendemos"
        hreflangEn="/areas-we-serve"
        schema={schema}
      />

      {/* Hero */}
      <section className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Atendiendo Fort Worth y 53 Ciudades Cercanas
            </h1>
            <p className="text-xl text-teal-100 mb-6">
              Atención dental galardonada — desde DFW hasta Hill Country. Únete a los más de 1,100 pacientes que nos han calificado con 5 estrellas en Google.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href={telLink}
                className="inline-flex items-center bg-white text-teal-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
              >
                <Phone className="w-5 h-5 mr-2" />
                {displayNumber}
              </a>
              <Link
                to="/es/contacto"
                className="inline-flex items-center bg-teal-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-400 transition border border-teal-400"
              >
                Reservar Cita
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive map in Spanish */}
      <AreasMap lang="es" />

      {/* Bottom CTA */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            ¿No Ves Tu Ciudad?
          </h2>
          <p className="text-gray-700 mb-6">
            Atendemos pacientes de todo el norte de Texas. Si tu ciudad no está en el mapa, llámanos — probablemente atendemos tu área también.
          </p>
          <a
            href={telLink}
            className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg text-lg font-bold"
          >
            Llamar al {displayNumber}
          </a>
        </div>
      </section>
    </>
  );
};

export default SpanishAreasPage;
